<template>
  <div id="adventure-story">
    <div class="content-container story-shell">
      <div class="content">
        <v-container v-if="gallery && post">
          <DraftBanner v-if="postStatus === 'draft'" />
          <div class="story-top-actions">
            <v-btn
              outlined
              class="back-link"
              :to="{ name: 'reckon-way-gallery', params: { gallerySlug: gallery.slug } }"
            >
              <v-icon left>mdi-arrow-left</v-icon>
              {{ gallery.title }}
            </v-btn>

            <v-btn
              outlined
              class="action-button"
              :to="{
                name: 'reckon-way-gallery',
                params: { gallerySlug: gallery.slug },
                hash: '#photo-gallery',
              }"
            >
              <v-icon left>mdi-image-multiple</v-icon>
              Photo Gallery
            </v-btn>
          </div>

          <TravelBlogArticle
            :post="post"
            :kicker="storyKicker"
          />

          <nav
            v-if="previousStory || nextStory"
            class="story-navigation"
            aria-label="Story navigation"
          >
            <v-btn
              v-if="previousStory"
              outlined
              class="story-navigation-link story-navigation-previous"
              :to="storyRoute(previousStory)"
            >
              <v-icon left>mdi-arrow-left</v-icon>
              <span>
                <small>Previous story</small>
                {{ previousStory.title }}
              </span>
            </v-btn>

            <v-spacer />

            <v-btn
              v-if="nextStory"
              color="error"
              class="story-navigation-link story-navigation-next"
              :to="storyRoute(nextStory)"
            >
              <span>
                <small>Next story</small>
                {{ nextStory.title }}
              </span>
              <v-icon right>mdi-arrow-right</v-icon>
            </v-btn>
          </nav>
        </v-container>

        <v-container v-else>
          <v-btn
            outlined
            class="back-link mb-6"
            :to="missingStoryBackRoute"
          >
            <v-icon left>mdi-arrow-left</v-icon>
            {{ missingStoryBackLabel }}
          </v-btn>
          <div class="missing-story">{{ missingStoryMessage }}</div>
        </v-container>
      </div>
    </div>

    <AppFooter></AppFooter>
  </div>
</template>

<script>
import {
  galleryBySlug,
  getBlogPostBySlug,
  getBlogPostSlug,
  getGalleryBlogPosts,
} from '../data/adventureGalleries';
import AppFooter from '../components/Footer.vue';
import TravelBlogArticle from '../components/TravelBlogArticle.vue';
import DraftBanner from '../components/draft/DraftBanner.vue';
import { canViewContent } from '../lib/draftMode';

export default {
  name: 'AdventureStoryView',
  components: { AppFooter, TravelBlogArticle, DraftBanner },
  computed: {
    gallery() {
      return galleryBySlug[this.$route.params.gallerySlug];
    },
    storyResult() {
      const narrative = this.$store.getters.visibleRemoteContent.narratives.find((item) => (
        item.eventSlug === this.$route.params.gallerySlug
        && item.slug === this.$route.params.storySlug
      ));
      if (narrative) return { post: narrative.article, narrative };
      return getBlogPostBySlug(this.gallery, this.$route.params.storySlug);
    },
    post() {
      const { post } = this.storyResult;
      return post && canViewContent(
        this.postStatus,
        this.$store.getters.draftModeEnabled,
        this.$store.getters.isAuthorizedReckonUser,
      ) ? post : null;
    },
    postStatus() {
      const { post, narrative } = this.storyResult;
      if (!post) return undefined;
      if (narrative) return narrative.status;
      return post.status || (post.released === false || this.gallery?.storiesComingSoon ? 'draft' : 'published');
    },
    storyKicker() {
      return this.gallery ? `${this.gallery.meta} Story` : 'Adventure Story';
    },
    galleryNarratives() {
      if (!this.gallery) return [];

      return this.$store.getters.visibleRemoteContent.narratives
        .filter((item) => item.eventSlug === this.gallery.slug);
    },
    storySequence() {
      if (!this.gallery) return [];

      if (this.galleryNarratives.length) {
        const remoteSlugs = new Set(this.galleryNarratives.map((item) => item.slug));
        const localPublishedStories = getGalleryBlogPosts(this.gallery)
          .filter((item) => !remoteSlugs.has(getBlogPostSlug(item)))
          .filter((item) => canViewContent(
            item.status || (item.released === false || this.gallery.storiesComingSoon ? 'draft' : 'published'),
            false,
            false,
          ));

        return [...localPublishedStories, ...this.galleryNarratives]
          .filter((item) => Number.isFinite(item.sequence ?? item.chapterNumber))
          .sort((left, right) => (
            (left.sequence ?? left.chapterNumber) - (right.sequence ?? right.chapterNumber)
          ));
      }

      return getGalleryBlogPosts(this.gallery).filter((item) => canViewContent(
        item.status || (item.released === false || this.gallery.storiesComingSoon ? 'draft' : 'published'),
        this.$store.getters.draftModeEnabled,
        this.$store.getters.isAuthorizedReckonUser,
      ));
    },
    currentStoryIndex() {
      return this.storySequence.findIndex((item) => (
        this.storySlugFor(item) === this.$route.params.storySlug
        || (item.legacySlugs || []).includes(this.$route.params.storySlug)
      ));
    },
    previousStory() {
      if (this.currentStoryIndex <= 0) return null;

      const current = this.storySequence[this.currentStoryIndex];
      const previous = this.storySequence[this.currentStoryIndex - 1];
      const currentSequence = current.sequence ?? current.chapterNumber;
      const previousSequence = previous.sequence ?? previous.chapterNumber;
      return Number.isFinite(currentSequence) && currentSequence - previousSequence !== 1
        ? null
        : previous;
    },
    nextStory() {
      if (this.currentStoryIndex < 0 || this.currentStoryIndex >= this.storySequence.length - 1) return null;

      const current = this.storySequence[this.currentStoryIndex];
      const next = this.storySequence[this.currentStoryIndex + 1];
      const currentSequence = current.sequence ?? current.chapterNumber;
      const nextSequence = next.sequence ?? next.chapterNumber;
      return Number.isFinite(currentSequence) && nextSequence - currentSequence !== 1
        ? null
        : next;
    },
    missingStoryBackRoute() {
      if (!this.gallery) {
        return { name: 'reckon-way' };
      }

      return {
        name: 'reckon-way-gallery',
        params: { gallerySlug: this.gallery.slug },
      };
    },
    missingStoryBackLabel() {
      return this.gallery ? this.gallery.title : 'The Reckon Way';
    },
    missingStoryMessage() {
      return 'Story not found.';
    },
  },
  methods: {
    storySlugFor(story) {
      return story.slug || getBlogPostSlug(story.article || story);
    },
    storyRoute(story) {
      return {
        name: 'reckon-way-story',
        params: {
          gallerySlug: this.gallery.slug,
          storySlug: this.storySlugFor(story),
        },
      };
    },
  },
};
</script>

<style scoped>
.story-shell {
  background: #050505;
  padding-bottom: 60px;
}

.story-top-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 28px;
}

.back-link {
  border-color: #424242 !important;
  color: #fff !important;
}

.action-button {
  border-color: #e23232 !important;
  color: #fff !important;
}

.story-navigation {
  align-items: stretch;
  border-top: 1px solid #2a2a2a;
  display: flex;
  gap: 18px;
  margin-top: 48px;
  padding-top: 28px;
}

.story-navigation-link {
  height: auto !important;
  max-width: 380px;
  min-height: 68px;
  padding: 12px 18px !important;
  text-transform: none;
}

.story-navigation-link span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: normal;
}

.story-navigation-link small {
  display: block;
  font-size: 0.7rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.story-navigation-previous {
  border-color: #424242 !important;
  color: #fff !important;
  text-align: left;
}

.story-navigation-next {
  text-align: right;
}

.travel-blog {
  max-width: 860px;
}

.blog-kicker {
  color: #e23232;
  font-family: "Oswald", serif;
  font-size: 0.95rem;
  font-weight: 700;
  letter-spacing: 0;
  text-transform: uppercase;
}

.blog-title {
  color: #fff;
  font-family: "Oswald", serif;
  font-size: 2.6rem;
  font-weight: 900;
  letter-spacing: 0;
  line-height: 1.08;
  margin: 10px 0 14px;
}

.blog-lede {
  color: #d5d5d5;
  font-size: 1.25rem;
  line-height: 1.6;
  margin-bottom: 30px;
}

.blog-image-grid {
  display: grid;
  gap: 18px;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  margin: 28px 0 34px;
}

.blog-section-image-grid {
  margin: 22px 0 4px;
}

.blog-image-card {
  background-color: #0d0d0d;
  border: 1px solid #2a2a2a;
  border-radius: 8px;
  margin: 0;
  overflow: hidden;
}

.blog-image-card-wide {
  grid-column: 1 / -1;
}

.blog-image {
  display: block;
  height: auto;
  width: 100%;
}

.blog-image-caption {
  border-top: 1px solid #242424;
  color: #b8b8b8;
  font-size: 0.95rem;
  line-height: 1.45;
  margin: 0;
  padding: 12px 14px;
}

.blog-section {
  margin-top: 34px;
}

.blog-section-title {
  border-left: 5px solid #e23232;
  color: #fff;
  font-family: "Oswald", serif;
  font-size: 1.5rem;
  font-weight: 900;
  letter-spacing: 0;
  line-height: 1.18;
  margin-bottom: 14px;
  padding-left: 16px;
}

.blog-section-subheading {
  color: #fff;
  font-size: 1rem;
  font-weight: 800;
  letter-spacing: 0.02em;
  line-height: 1.45;
  margin: -4px 0 12px;
  text-transform: uppercase;
}

.blog-paragraph {
  color: #c9c9c9;
  font-size: 1.08rem;
  line-height: 1.75;
  margin-bottom: 16px;
}

.blog-fact-list {
  border: 1px solid #2a2a2a;
  border-radius: 8px;
  margin: 18px 0 4px;
  overflow: hidden;
}

.blog-fact-row {
  display: grid;
  grid-template-columns: minmax(180px, 0.8fr) 1fr;
}

.blog-fact-row + .blog-fact-row {
  border-top: 1px solid #242424;
}

.blog-fact-row dt,
.blog-fact-row dd {
  color: #d8d8d8;
  font-size: 1rem;
  line-height: 1.45;
  margin: 0;
  padding: 14px 16px;
}

.blog-fact-row dt {
  background: #101010;
  color: #fff;
  font-weight: 800;
}

.blog-fact-row dd {
  background: #080808;
}

.blog-fact-link {
  color: #fff !important;
  text-decoration: none;
}

.blog-fact-link:hover,
.blog-fact-link:focus {
  color: #e23232 !important;
}

.blog-bullet-list {
  color: #c9c9c9;
  font-size: 1.06rem;
  line-height: 1.7;
  margin: 4px 0 18px;
  padding-left: 24px;
}

.blog-callout {
  color: #e6e6e6;
  font-size: 1.03rem;
  font-style: italic;
  line-height: 1.65;
  margin: 8px 0 12px;
}

.blog-note {
  color: #e6e6e6;
  font-size: 1rem;
  font-style: italic;
  line-height: 1.5;
  margin: 12px 0 0;
}

.blog-subsections {
  display: grid;
  gap: 20px;
  margin-top: 18px;
}

.blog-subsection {
  border-left: 2px solid #2a2a2a;
  padding-left: 18px;
}

.blog-subsection-title {
  color: #fff;
  font-family: "Oswald", serif;
  font-size: 1.18rem;
  font-weight: 900;
  letter-spacing: 0;
  line-height: 1.2;
  margin: 0 0 10px;
}

.missing-story {
  color: #fff;
  font-size: 1.4rem;
}

@media (max-width: 600px) {
  .story-navigation {
    align-items: stretch;
    flex-direction: column;
  }

  .story-navigation .v-spacer {
    display: none;
  }

  .story-navigation-link {
    max-width: none;
    width: 100%;
  }

  .blog-title {
    font-size: 2rem;
  }

  .blog-lede {
    font-size: 1.1rem;
  }

  .blog-image-grid {
    grid-template-columns: 1fr;
  }

  .blog-fact-row {
    grid-template-columns: 1fr;
  }
}
</style>
